/* Programs landing + program detail. */
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";
import { Link } from "../../lib/router-compat.jsx";
import PublicShell from "../../components/shell/PublicShell.jsx";
import SectionRenderer, { HeroBlock } from "../../components/SectionRenderer.jsx";
import RichText from "../../ui/RichText.jsx";
import { SmartImage, Skeleton, EmptyState, Icon } from "../../ui/index.js";
import { usePage, usePrograms, useProgram } from "../../data/hooks.js";

export function ProgramsPage() {
  const { sections } = usePage("programs");
  const { programs, loading } = usePrograms({ publishedOnly: true });

  return (
    <PublicShell>
      <Helmet><title>Programs — Purple Angels Kids Foundation</title></Helmet>
      <SectionRenderer sections={sections} />
      {loading ? (
        <section className="section">
          <div className="container"><Skeleton h={320} /></div>
        </section>
      ) : programs.length === 0 ? (
        <section className="section">
          <div className="container"><EmptyState title="No programs yet" body="Check back soon." /></div>
        </section>
      ) : (
        programs.map((p, idx) => (
          <ProgramRow key={p.slug} program={p} warm={idx % 2 === 1} flip={idx % 2 === 1} />
        ))
      )}
    </PublicShell>
  );
}

/* A single program rendered as a full-width feature section, alternating the
   image side and background so the list reads as scrollable sections rather
   than a card grid. The image is constrained (object-fit: cover) so large
   uploads never overflow. */
function ProgramRow({ program, warm, flip }) {
  const to = `/programs/${program.slug}`;
  return (
    <section className={`section ${warm ? "section--warm" : ""}`}>
      <div className="container">
        <div className={`feature-row ${flip ? "feature-row--flip" : ""}`}>
          <Link to={to} className="feature-row__media" aria-label={program.title}>
            <SmartImage src={program.heroImage?.url} alt={program.heroImage?.alt || program.title} label={program.title} />
          </Link>
          <div className="feature-row__content">
            {program.isFeatured && <span className="t-eyebrow">Featured programme</span>}
            <h2 className="t-3xl" style={{ margin: 0 }}>{program.title}</h2>
            {program.summary && <p className="t-lg muted" style={{ margin: 0 }}>{program.summary}</p>}
            <Link to={to} className="btn btn--secondary"><Icon.ArrowRight size={16} /> Learn more</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ProgramDetailPage() {
  const { slug } = useParams();
  const { program, loading } = useProgram(slug);
  // null-safe: only fetches when a linked page exists
  const { sections: linkedSections } = usePage(program?.pageSlug || null);

  return (
    <PublicShell>
      {loading ? (
        <div className="container" style={{ padding: "64px 0" }}><Skeleton h={320} /></div>
      ) : !program ? (
        <div className="container" style={{ padding: "64px 0" }}>
          <EmptyState title="Program not found" body="This program may have been moved." action={<Link to="/programs" className="btn btn--secondary">All programs</Link>} />
        </div>
      ) : (
        <>
          <Helmet><title>{program.title} — Purple Angels Kids Foundation</title></Helmet>
          <HeroBlock section={{ eyebrow: "Program", headline: program.title, subheadline: program.summary, image: program.heroImage, ctaLabel: "Support this program", ctaHref: "/donate" }} />
          {program.bodyHtml && (
            <section className="section section--warm">
              <div className="container"><div className="prose"><RichText html={program.bodyHtml} className="" /></div></div>
            </section>
          )}
          {program.pageSlug && linkedSections?.length > 0 && <SectionRenderer sections={linkedSections} />}
          <section className="section">
            <div className="container center">
              <Link to="/donate" className="btn btn--primary btn--lg"><Icon.Heart size={16} /> Support our work</Link>
            </div>
          </section>
        </>
      )}
    </PublicShell>
  );
}

export default ProgramsPage;
